<template>
  <div>
    <table class="tw:min-w-full tw:border-none" v-if="!isLoading && !isUserDetailsLoading">
      <thead class="tw:hidden tw:md:table-header-group">
        <tr class="tw:border-b">
          <th
            v-for="column in columns"
            :key="column.prop"
            class="tw:p-5 tw:pl-0 tw:border-none tw:bg-white tw:text-left tw:text-sm tw:leading-4 tw:font-bold tw:text-gray-700"
            :class="`tw-w-[${column.width}]`"
          >
            <span>{{ $t(column.label) }}</span>
          </th>
        </tr>
      </thead>
      <tbody class="tw:bg-white">
        <!-- current user -->
        <tr class="tw:border-none tw:text-sm tw:font-medium tw:flex tw:md:table-row tw:flex-col">
          <td class="tw:w-full tw:md:w-[40%] tw:border-none tw:p-5 tw:pl-0">
            <div class="tw:flex tw:flex-row tw:md:hidden tw:justify-between tw:w-full tw:bg-blue-200 tw:p-2">
              <span>{{ $t('form.labels.accounts') }}{{ ': ' }}</span>
              <span>{{ userDetails.email }}</span>
            </div>
            <span class="tw:md:flex tw:hidden">{{ userDetails.email }}</span>
          </td>
          <td class="tw:w-full tw:md:w-[30%] tw:border-none tw:p-5 tw:pl-0">
            <div class="tw:flex tw:flex-row tw:md:hidden tw:justify-between tw:w-full tw:px-2">
              <span>{{ $t('form.labels.role') }}{{ ': ' }}</span>
              <span>{{ getRoleLabel(userRole) }}</span>
            </div>
            <span class="tw:md:flex tw:hidden">{{ getRoleLabel(userRole) }}</span>
          </td>
          <td class="tw:w-[30%] tw:border-none tw:md:flex tw:hidden">&nbsp;</td>
        </tr>
        <!-- Rest users -->
        <tr
          v-for="user in organisationUsersWithoutMe"
          :key="user.userId"
          class="tw:text-sm tw:border-top tw:md:border-none tw:flex tw:md:table-row tw:flex-col"
        >
          <td class="tw:border-none tw:p-5 tw:pl-0">
            <div class="tw:flex tw:flex-row tw:md:hidden tw:justify-between tw:w-full tw:bg-blue-200 tw:p-2">
              <span>{{ $t('form.labels.accounts') }}{{ ': ' }}</span>
              <span>{{ user.email }}</span>
            </div>
            <span class="tw:md:flex tw:hidden">{{ user.email }}</span>
          </td>
          <td class="tw:border-none tw:p-5 tw:pl-0">
            <div class="tw:flex tw:flex-row tw:md:hidden tw:justify-between tw:items-center tw:w-full tw:px-2">
              <span>{{ $t('form.labels.role') }}{{ ': ' }}</span>
              <div
                v-if="!isSupportRole(user.role)"
                class="tw:relative tw:flex tw:justify-start tw:items-center tw:w-[150px] tw:px-3 tw:py-2 tw:border tw:rounded-lg tw:border-gray-500"
              >
                <select
                  class="tw:appearance-none tw:focus:outline-hidden tw:w-[150px]"
                  @change.prevent="onUserRoleChange($event, user.userId)"
                >
                  <option v-for="role in availableUserRoles" :key="role" :value="role" :selected="user.role == role">
                    {{ $t('form.labels.user_role.' + role) }}
                  </option>
                </select>
                <ChevronRight class="tw:absolute arrow-dropdown tw:size-6 tw:fill-[#6C707A]" />
              </div>
              <span v-else>{{ getRoleLabel(user.role) }}</span>
            </div>
            <div
              v-if="!isSupportRole(user.role)"
              class="tw:md:flex tw:hidden tw:relative tw:flex tw:justify-start tw:items-center tw:w-[150px]"
            >
              <select
                class="tw:appearance-none tw:focus:outline-hidden tw:w-[150px] tw:px-4 box-size-36 tw:text-sm tw:rounded-lg tw:border tw:border-gray-500"
                @change.prevent="onUserRoleChange($event, user.userId)"
              >
                <option v-for="role in availableUserRoles" :key="role" :value="role" :selected="user.role == role">
                  {{ $t('form.labels.user_role.' + role) }}
                </option>
              </select>
              <ChevronRight class="tw:absolute arrow-dropdown tw:size-6 tw:fill-[#6C707A]" />
            </div>
            <span v-else class="tw:md:flex tw:hidden">{{ getRoleLabel(user.role) }}</span>
          </td>
          <td class="tw:border-none tw:p-5 tw:pl-0 tw:flex tw:justify-end">
            <button
              type="button"
              class="tw:flex tw:items-center tw:border tw:p-2 tw:rounded-lg tw:border-gray-800 tw:md:border-none"
              @click.prevent="openDeletingModal(user.userId)"
            >
              <Delete class="tw:size-6" />
              <span class="tw:ml-2">
                {{ $t('form.info.title_remove_user') }}
              </span>
            </button>
          </td>
        </tr>
      </tbody>
    </table>
    <RemoveUserModal
      :isOpen="isDeletingModalOpen"
      :closeModal="closeDeletingModal"
      :loading="removeUserFromOrgLoading"
      :isInvitationRemoval="false"
      @removeUser="onRemoveUser"
    />
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import Delete from '@/components/icons/Delete.svg?component'
import RemoveUserModal from './RemoveUserModal.vue'
import ChevronRight from '@/components/icons/ChevronRight.svg?component'
import {
  useOrganisationUsers,
  updateUserRoleMutation,
  removeUserFromOrgMutation,
} from '@/components/settings/teamSettings/queries'
import useToastNotifications from '@/components/notifications/useToastNotifications'
import { useUserDetails } from '@/layouts/queries'
import { useSessionValues } from '@/composables/useSessionValues'
import { orderBy } from '@/utils/Utility'

const { t } = useI18n()
const { displayNotification } = useToastNotifications()

const isDeletingModalOpen = ref(false)
const userIdToRemove = ref(null)
const columns = ref([
  {
    label: 'form.labels.accounts',
    prop: 'accounts',
    width: '40%',
  },
  {
    label: 'form.labels.role',
    prop: 'role',
    width: '30%',
  },
  {
    label: '',
    prop: 'remove',
    width: '30%',
  },
])
const availableUserRoles = ref(['admin', 'normal'])

const isSupportRole = role => String(role ?? '').toLowerCase() === 'support'

const getRoleLabel = role => {
  if (isSupportRole(role)) return t('tbs_role_hotelier_support')

  return t('form.labels.user_role.' + role)
}

const { userRole, userId, selectedOrganisationId } = useSessionValues()
const { userDetails, isUserDetailsLoading } = useUserDetails(userId)
const organisationUsersWithoutMe = computed(() => {
  if (!Array.isArray(userOrganisationData.value)) return []

  let filteredUsers = userOrganisationData.value.filter(organisationUser => {
    return userId.value !== organisationUser.userId
  })

  return orderBy(filteredUsers, 'email')
})

const { isLoading, userOrganisationData } = useOrganisationUsers()
const { updateUserRole } = updateUserRoleMutation()
const { removeUserFromOrgLoading, removeUserFromOrg } = removeUserFromOrgMutation()

const openDeletingModal = userId => {
  isDeletingModalOpen.value = true
  userIdToRemove.value = userId
}

const closeDeletingModal = () => {
  isDeletingModalOpen.value = false
  userIdToRemove.value = null
}

const onUserRoleChange = async (event, userId) => {
  const role = event.target.value
  updateUserRole(
    { organisationId: selectedOrganisationId.value, userId, role },
    {
      onSuccess: () => {
        displayNotification({
          message: 'form.messages.success.role_changed',
          isTranslationKey: true,
          isHtml: false,
          type: 'success',
        })
      },
      onError: () => {
        displayNotification({
          message: 'form.messages.error.unknown',
          isTranslationKey: true,
          isHtml: false,
          type: 'error',
        })
      },
    }
  )
}

const onRemoveUser = async () => {
  removeUserFromOrg(
    { userId: userIdToRemove.value },
    {
      onSuccess: () => {
        displayNotification({
          message: 'form.messages.success.user_removed',
          isTranslationKey: true,
          isHtml: false,
          type: 'success',
        })
        closeDeletingModal()
      },
      onError: () => {
        displayNotification({
          message: 'form.messages.error.unknown',
          isTranslationKey: true,
          isHtml: false,
          type: 'error',
        })
        closeDeletingModal()
      },
    }
  )
}
</script>
<style scoped>
.arrow-dropdown {
  top: 50%;
  transform: translateY(-45%) rotate(90deg);
  right: 6px;
  pointer-events: none;
}
</style>
